import React, { useRef, useEffect, useState, useCallback, useId } from 'react'
import { Box, Stack, MenuItem, Select, useMediaQuery, useTheme } from '@mui/material'
import { FS, SP, TOUCH_TARGET_COARSE, FOCUS_RING, hoverOnly } from '../../theme/tokens'
import useOverflowMask from './useOverflowMask'
// 🔴 G4a-Nachbesserung 1 (Fotobefund 3): der Reiterzaehler ist eine ANZEIGEZAHL
// wie jede andere im Haus und geht deshalb durch `utils/format.js` (Owner-Entscheid
// 2026-10-06, feste Sprache `de-DE`) — genau wie `ResultCount` daneben
// („1.391 messages") und die Fusszeile. Bis hierher stand die rohe Zahl
// („1391") NEBEN der formatierten („1.391") in derselben Ansicht.
import { formatInt } from '../../utils/format'

/**
 * UnderlineTabs — animated underline tab switcher.
 *
 * @description
 *   Compact tab row with a full-width 2px divider and a sliding primary-colour
 *   indicator (the CaseTimeline tab bar style). Use instead of MUI <Tabs>
 *   wherever a view segments one list by state. The row scrolls inside its own
 *   container instead of wrapping or pushing the page wider — the DEFAULT on a
 *   coarse pointer, opt-in on a fine one (`scrollable`), as the pattern library
 *   asks above four tabs on a phone (N62, 2026-09-27; M1, 2026-10-09).
 *
 *   Keyboard (N72, 2026-09-30): an ARIA tablist, but a partial one. Built:
 *   `role="tablist"` / `role="tab"` / `aria-selected`; the tab stop is the
 *   SELECTED one. Not built: `aria-controls` / `role="tabpanel"` (no panel) and
 *   a name for the tablist itself — UNLESS `ariaLabel` names it, or `withPanel`
 *   asks for the panel semantics (G0.7, 2026-10-11); without those props the
 *   tree is the old one. 🔴 The name comes ONLY from `ariaLabel`: `withPanel`
 *   does NOT name the tablist, and `withPanel` without `ariaLabel` is a defect
 *   at the call site — the component THROWS (same contract as `RefreshButton`'s
 *   missing `ariaLabel` and `EmptyState`'s missing `reason`). A `role="tablist"`
 *   with no accessible name is an APG deviation, and two tab rows on one page
 *   are then indistinguishable to a screen reader.
 *   Arrows move focus and wrap, Home / End jump to the ends; focus does NOT
 *   select (manual activation, like MUI `Tabs`); Enter / Space select; counted
 *   tabs read "<label>, <count>".
 *
 *   Touch (F-2, R16 W-2, 2026-10-08): on a coarse pointer each tab keeps the
 *   44 px floor (`TOUCH_TARGET_COARSE`) — a tab is a control, and the dense
 *   desktop row (~32 px) sits below the pattern library's house rule for
 *   non-MUI controls (≥ 44 px on a coarse pointer; the AA minimum underneath is
 *   24 px, so the dense row breaks no norm). The floor is pointer-dependent, not
 *   width-dependent: a narrow window with a FINE pointer keeps the dense row
 *   (Plan §1 F1 — density follows the primary pointer). Desktop unchanged.
 *
 *   G0.7 (UI2 Teil 10, 2026-10-11) — four additions, every one of them OPT-IN;
 *   without the new props the rendered tree is the one from before:
 *   • the soft edge at an overflowing end now comes from `useOverflowMask`
 *     (G0.1) instead of the inline copy — same maths, same 24 px (`SP.md`), and
 *     `value` rides in as the hook's `deps` (a longer tab grows the row inside
 *     an unchanged box; the hook's `ResizeObserver` cannot see that);
 *   • `withPanel` gives the row the panel semantics the concept §5.4 asks for —
 *     an `id` per tab, `aria-controls` on the SELECTED tab only (a link into
 *     the void is an invalid attribute, `InvoiceList.jsx` `Reiterleiste` says
 *     the same) and a `role="tabpanel"` around `children`. It does NOT name the
 *     tablist: that name comes from `ariaLabel`, which `withPanel` therefore
 *     requires (a nameless `tablist` is the APG deviation this prop would
 *     otherwise introduce, so the component throws without one).
 *     🔴 The duty hangs on `withPanel` alone, not on the branch: the guard is
 *     branch-blind — it fires before the row decides between tabs and the
 *     selector, so a caller that would land in the selector still owes the
 *     name. The selector BRANCH names itself `"Tabs"` (the `ariaLabel` default);
 *     the fallback covers only calls WITHOUT `withPanel` (a nameless control is a finding there too).
 *     Without `withPanel` the row is the old, unnamed tablist.
 *     That is the §5.4 proof; the registry boundary "view tabs with a panel =
 *     MUI `Tabs`" is dissolved by it, but only from the first view card that
 *     also carries the Regel-16 round (concept §5.4, §0.2);
 *   • `counts` is the ALIGNED counter form: the pill reserves the width of the
 *     widest count (`tabular-nums` + a `ch` floor), so tabs stop jumping when
 *     the active tab changes (concept §5.2, R2). The `{label, count}` form
 *     keeps its old rendering untouched — switching the existing seven callers
 *     over is a view card (G1–G15), not this component;
 *   • eight or more tabs become a `Select` on a COARSE pointer BELOW `sm`: a tab
 *     row that long is a list, not a row (concept N5.5 "Mehr als sieben Reiter
 *     … Auswähler"). 🔴 N5.5 binds the whole clause to "bei grobem Zeiger und
 *     unter `sm`" — both halves belong to it, so a coarse pointer at `sm` and up
 *     (a tablet) keeps the scrolling row, and so does a fine pointer at any
 *     width; a `Select` there would be the worse control. Fires for no caller
 *     today (seven callers, six tabs at most — `RMAInbox.jsx`).
 *
 * @useWhen
 *   - Segmenting an inbox or list by status (Unread / All / Errors …)
 *   - Tab rows in detail views and dashboards that want the sliding underline
 *   - More than four tabs on a FINE pointer that must fit a phone width → `scrollable`
 *   - A row whose tabs each own a panel → `withPanel` + `ariaLabel` (tablist ↔ tabpanel)
 *
 * @dontUseFor
 *   - Navigation between routes — use react-router links; a route tab bar is
 *     navigation, not a content row (concept §5.1), and its mobile form is a
 *     navigation selector (N5.6), which is not this component
 *   - A small exclusive option set as a control — use SegmentedSlider
 *   - A long exclusive option set that is NOT a row of views — use
 *     BottomSheetSelect (or SegmentedSlider up to five). A row of VIEWS with
 *     eight or more tabs does NOT go there: this component becomes its own
 *     `Select` on a coarse pointer below `sm` (see above), so the caller passes
 *     the tabs here and nothing else
 *
 * @related
 *   - SegmentedSlider — 2–5 exclusive options as a control, not a row of views
 *   - FilterPillRow — scrollable multi-select pills, not a single active view
 *   - useOverflowMask — the soft edge this row wears (BS15)
 *
 * @reuseHints
 *   - POInbox tab row when it grows past four tabs (pass `scrollable`)
 *   - Any per-status list on mobile that today wraps its tab row
 *   - A list view whose tabs own their panel (`withPanel` + `ariaLabel` + `panelIdPrefix`)
 *     — 🔴 two tab rows on ONE page each need their OWN `ariaLabel`: the name is
 *     the only thing that tells them apart, and `withPanel` does not invent one
 *
 * @props
 *   tabs          Array<string | {label, count?}>  Tab definitions; `count` renders a pill
 *   value         number                           Active tab index
 *   onChange      (index: number) => void          Called on tab click, Enter / Space.
 *                                                  Arrow keys move focus only (manual activation)
 *   scrollable    boolean (default: coarse pointer) Row scrolls horizontally in its container;
 *                                                  tabs stop wrapping and never shrink
 *   withPanel     boolean (default: false)         Panel semantics: gives each tab an id, puts
 *                                                  `aria-controls` on the selected tab, wraps
 *                                                  `children` in a `role="tabpanel"`. Does NOT
 *                                                  name the tablist — it requires `ariaLabel` for
 *                                                  that and throws without one
 *   panelIdPrefix string (default: a React `useId`) Prefix of `-tab-<i>` / `-panel-<i>`; read only
 *                                                  with `withPanel`
 *   counts        Array<number|null>               Counts parallel to `tabs`, the ALIGNED form:
 *                                                  the pill reserves the widest count's width.
 *                                                  🔴 Gerendert durch `utils/format.js`
 *                                                  (`formatInt`, de-DE) — wie `ResultCount`
 *                                                  und die Fußzeile; die Reserve rechnet in den
 *                                                  Stellen der FORMATIERTEN Zahl.
 *                                                  🔴 Diese Form traegt auch die
 *                                                  BREITENRESERVE der Beschriftung:
 *                                                  der aktive Reiter steht in 600,
 *                                                  die uebrigen in 400, und fetter
 *                                                  Text ist breiter — ohne einen
 *                                                  unsichtbaren fetten Zwilling
 *                                                  aendert der Reiter beim Wechsel
 *                                                  seine eigene Breite (N5.3/R2,
 *                                                  „Reiter springen nicht"; P11
 *                                                  misst ±1 px). Dieselbe Reserve
 *                                                  haelt die Zaehlerpille in einer
 *                                                  festen Schnittstelle, weil `ch`
 *                                                  an der Schrift des Elements
 *                                                  haengt. Die alte
 *                                                  `{label, count}`-Form ist
 *                                                  davon AUSGENOMMEN (s. `stellen`)
 *   ariaLabel     string                           Accessible name of the tablist (and of the
 *                                                  selector when the fallback fires). REQUIRED
 *                                                  together with `withPanel`
 *   panelAriaDescribedBy string (optional)         `aria-describedby` for the `role="tabpanel"`
 *                                                  (N5.5/Rechnungen S2-NB3, 2026-11-01): the
 *                                                  id of the sentence that describes the panel's
 *                                                  content. Read only with `withPanel`; without
 *                                                  it the panel carries no such attribute, so
 *                                                  the tree of every other caller is unchanged
 *   sx            SxProps (optional)               Applied to the root container
 *
 * Design tokens
 *   FS.base  (13 px) — tab label size
 *   SP.sm    (12 px) — horizontal tab padding
 *   SP.md    (24 px) — soft edge of the overflow mask (via `useOverflowMask`)
 *   TOUCH_TARGET_COARSE — 44 px Trefferhoehe auf grobem Zeiger (Haus-Regel; F-2)
 *   primary.main    — active colour + indicator colour
 *   divider         — 2px full-width bottom line
 */
export default function UnderlineTabs({
  tabs = [],
  value = 0,
  onChange,
  scrollable,
  sx,
  withPanel = false,
  panelIdPrefix,
  counts,
  ariaLabel,
  panelAriaDescribedBy,
  children,
}) {
  const tabRefs = useRef([])
  const rowRef = useRef(null)
  const [ind, setInd] = useState({ left: 0, width: 0 })
  const [ready, setReady] = useState(false)
  // 🔴 M1 (UI-Durchgang 2, Teil 5, 2026-10-09): die Reiterreihe ist mobil eine
  // ROLLFLAECHE, keine Umbruchflaeche. `scrollable` ist deshalb auf einem groben
  // Zeiger die VORGABE — eine Stelle statt sieben Einzelentscheidungen, und
  // dieselbe Achse wie der Touch-Boden darunter (`pointer: coarse`, NICHT
  // `any-pointer`; Hausentscheid F1, `theme/tokens.js:433-442`). Ein ausdruecklich
  // uebergebenes `scrollable` schlaegt die Vorgabe in beide Richtungen.
  const theme = useTheme()
  const groberZeiger = useMediaQuery('(pointer: coarse)')
  // 🔴 G0.7 Runde 2 (R15 W2): N5.5 bindet die Auswaehler-Regel woertlich an
  // „bei grobem Zeiger UND unter `sm`" (`concepts/tabellen-filter-suche.md:457`).
  // Beide Haelften gehoeren dazu — ein Tablet (grober Zeiger ab `sm`) behaelt die
  // rollende Reihe, und auf einem feinen Zeiger ohnehin. Eine zweite, eigene
  // Abfrage: die Zeigerachse steht darueber, die Breitenachse hier.
  const unterSm = useMediaQuery(theme.breakpoints.down('sm'))
  const rollt = scrollable ?? groberZeiger
  // 🔴 G0.7 (2026-10-11): der Ueberlaufhinweis kommt aus `useOverflowMask` (G0.1) —
  // dieselbe Rechnung (1 px Toleranz, je Rand getrennt), dieselbe weiche Kante
  // (`SP.md` = 24 px, vorher als Literal hier), aber EINE Umsetzung statt vier.
  // `value` faehrt als `deps` mit: ein laengerer Reiter laesst den Inhalt wachsen,
  // ohne die Box zu aendern — genau das sieht der `ResizeObserver` des Hooks nicht.
  // Angewandt wird die Maske nur, wenn die Zeile rollt (unten am Stack); der Hook
  // liefert bei fehlendem Ueberlauf ohnehin `{}`.
  const { maskSx } = useOverflowMask(rowRef, { deps: [value] })
  // 🔴 G0.7: Kennungen NUR mit `withPanel`. Ohne die Prop bleibt der Baum der alte —
  // kein `id`, kein `aria-controls`, kein `tabpanel` (Regel 17 §1: „unveraendert"
  // und „vakuos" faellt sonst gleich aus).
  const autoId = useId()
  const basis = panelIdPrefix || autoId
  const reiterId = i => `${basis}-tab-${i}`
  const panelId = i => `${basis}-panel-${i}`
  // 🔴 G0.7: acht oder mehr Reiter sind auf einem groben Zeiger UNTER `sm` eine
  // LISTE, keine Reihe (Konzept N5.5, „Mehr als sieben Reiter … → Auswähler",
  // dessen ganzer Satz an „bei grobem Zeiger und unter `sm`" haengt). Auf einem
  // FEINEN Zeiger — und auf einem groben ab `sm` — bleibt es die rollende Reihe:
  // ein Select ist dort die schlechtere Bedienung. Feuert heute bei keinem Aufrufer.
  const auswahl = tabs.length >= AUSWAHL_AB && groberZeiger && unterSm
  // Der Zaehler hat zwei Formen: `counts` (ausgerichtet, Platz reserviert) und die
  // alte `{label, count}`. Die alte Form behaelt ihre Darstellung unveraendert —
  // eine Reserve dort waere eine sichtbare Aenderung an sieben bestehenden Ansichten.
  const beschriftung = tab => (typeof tab === 'string' ? tab : tab?.label)
  const zaehler = (tab, i) => (Array.isArray(counts)
    ? counts[i]
    : (tab && typeof tab === 'object' ? tab.count : undefined))
  // Stellen der breitesten Zahl; `tabular-nums` macht jede Ziffer gleich breit, die
  // `ch`-Untergrenze reserviert so viele Stellen. 0 = keine Reserve (alte Form).
  // 🔴 G4a-Nachbesserung 1: gezaehlt werden die Stellen der FORMATIERTEN Zahl
  // („1.391" = 5), nicht die der rohen („1391" = 4) — sonst waere die Reserve
  // schmaler als die Pille und die Reiter spraengen (P11 misst genau das).
  const stellen = Array.isArray(counts)
    ? counts.reduce((m, n) => (n == null ? m : Math.max(m, formatInt(n).length)), 1)
    : 0
  // 🔴 G4a-Nachbesserung 4 (P11): die AUSGERICHTETE Form reserviert ihre Breite,
  // die alte `{label, count}`-Form behaelt ihre Darstellung (s. `stellen` oben).
  // Die beiden Reserven unten haengen deshalb an DIESEM Schalter und nicht am
  // Baustein: eine Reservierung ohne `counts` waere eine sichtbare Aenderung an
  // den sieben Bestandsaufrufern (dieselbe Grenze, die `stellen` schon zieht).
  const reserviert = stellen > 0
  // 🔴 G4a-Nachbesserung 1 (Fotobefund 1). Der Aktiv-Strich wird aus den
  // GEMESSENEN Reiterkaesten gerechnet (`measure`). Die Breite eines Reiters
  // haengt aber auch an seinem INHALT: kommen die Zaehler asynchron nach, waechst
  // jeder Reiter — und alle Reiter rechts von ihm wandern. Beobachtet wurde bis
  // hier nur die GROESSE des Behaelters (`ResizeObserver`) und `value`; eine
  // gefuellte Zaehlerpille aendert die Behaeltergroesse NICHT (die Zeile ist
  // voll breit, die Kinder wachsen nach innen). Der Strich blieb deshalb auf der
  // alten Stelle stehen — gemessen am 2026-10-14 an `/rma/inbox`: aktiv war
  // „Errors", der Strich stand unter „Archived" (eine Reiterbreite daneben), weil
  // die fuenf Zaehler links davon erst nach dem ersten Aufbau eintrafen.
  // Derselbe Schluessel traegt auch die `stellen`-Reserve (sie aendert die
  // Pillenbreite ebenfalls).
  const inhaltsSchluessel = tabs
    .map((tab, i) => `${beschriftung(tab)}·${zaehler(tab, i) ?? ''}`)
    .join('|') + `·${stellen}`

  // Measure the DOM offset of the active tab. With `rollt` the row is the
  // scroll container, so the indicator (which lives outside it, on the divider)
  // subtracts the current scroll offset — und wird zusaetzlich auf den
  // sichtbaren Bereich geklammert (Begruendung unten).
  const measure = useCallback(() => {
    const el = tabRefs.current[value]
    if (!el) return
    const row = rollt ? rowRef.current : null
    const links = el.offsetLeft - (row ? row.scrollLeft : 0)
    const rechts = links + el.offsetWidth
    // 🔴 M1 (2026-10-09): die Geometrie wird auf den SICHTBAREN Bereich der
    // Zeile geklammert. Bis heute leistete das ein `overflow: 'hidden'` am
    // aeusseren Kasten — und schnitt dabei den Strich selbst weg, weil der auf
    // dem 2-px-Rand sitzt (`bottom: -2`) und damit ausserhalb der Polsterkante
    // liegt (Befund: der Aktiv-Strich war im rollenden Modus unsichtbar).
    // Geklammert verbreitert ein herausgerollter Reiter die Seite nicht mehr —
    // ein ganz herausgerollter hat Breite 0 —, und der sichtbare Teil des
    // Strichs bleibt sichtbar.
    setInd(row
      ? { left: Math.max(0, links), width: Math.max(0, Math.min(row.clientWidth, rechts) - Math.max(0, links)) }
      : { left: links, width: el.offsetWidth })
    setReady(true)
  }, [value, rollt])

  // Re-measure when active tab changes (+ after first paint via rAF); bring the
  // active tab into view when the row scrolls.
  useEffect(() => {
    if (rollt) {
      const el = tabRefs.current[value]
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ block: 'nearest', inline: 'nearest' })
      }
    }
    measure()
    const raf = requestAnimationFrame(measure)
    return () => cancelAnimationFrame(raf)
  }, [measure, rollt, value])

  // 🔴 G4a-Nachbesserung 1 (Fotobefund 1): der zweite Ausloeser des Nachmessens —
  // der INHALT (Beschriftung, Zaehlerpille, Reserve). `value` allein genuegt nicht:
  // treffen die Zaehler nach dem ersten Aufbau ein, waechst jeder Reiter, und der
  // Strich bleibt auf der alten Geometrie stehen. `inhaltsSchluessel` ist ein
  // STRING, nicht das Array — ein frisches `tabs`-Array je Render wuerde sonst bei
  // jedem Render nachmessen.
  useEffect(() => {
    measure()
  }, [measure, inhaltsSchluessel])

  // Re-measure on container resize (e.g. panel expand / window resize). Die Zeile
  // wird mitbeobachtet: die Strichgeometrie haengt an ihrer Breite. 🔴 Den
  // Ueberlaufhinweis beobachtet der Hook selbst (`useOverflowMask`, G0.1).
  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') return undefined
    const ro = new ResizeObserver(measure)
    const container = tabRefs.current[0]?.parentElement?.parentElement
    if (container) ro.observe(container)
    if (rowRef.current) ro.observe(rowRef.current)
    return () => ro.disconnect()
  }, [measure])

  // Re-measure on scroll, so the indicator follows the active tab. Den
  // Ueberlaufhinweis fuehrt der Hook an derselben Zeile nach.
  useEffect(() => {
    if (!rollt) return undefined
    const row = rowRef.current
    if (!row) return undefined
    row.addEventListener('scroll', measure, { passive: true })
    return () => row.removeEventListener('scroll', measure)
  }, [measure, rollt])

  // ARIA-Reiter: Pfeiltasten bewegen den Fokus, Pos1/Ende springen an die Enden.
  // Bewusst KEINE Auswahl beim Fokus (manuelle Aktivierung): der Wechsel stoesst in
  // beiden Aufrufern einen Serverabruf an, und die WAI-ARIA-Praxis empfiehlt dort die
  // manuelle Aktivierung. Enter und Leertaste waehlen (unten am Reiter).
  // Der Umbruch an den Enden entspricht MUI `Tabs` (`moveFocus`).
  const onListKeyDown = useCallback(e => {
    const n = tabs.length
    if (!n) return
    // Modifier-Schranke, woertlich wie MUI (`@mui/material/Tabs/Tabs.js:703`):
    // mit Alt, Shift, Strg oder Cmd gedrueckt ist die Taste ein Browser- oder
    // Betriebssystemkuerzel und keine Reiterbewegung. Ohne diese Zeile wuerde
    // Alt+Pfeil links die Auswahl im Browser zuruecknehmen UND den Fokus
    // verschieben.
    if (e.altKey || e.shiftKey || e.ctrlKey || e.metaKey) return
    const list = e.currentTarget
    const current = tabRefs.current.findIndex(el => el === list.ownerDocument.activeElement)
    if (current < 0) return
    let next = null
    if (e.key === 'ArrowRight') next = (current + 1) % n
    else if (e.key === 'ArrowLeft') next = (current - 1 + n) % n
    else if (e.key === 'Home') next = 0
    else if (e.key === 'End') next = n - 1
    if (next == null) return
    // Auch wichtig, wenn `scrollable`: sonst rollt die Zeile statt zu wechseln.
    e.preventDefault()
    tabRefs.current[next]?.focus()
  }, [tabs.length])

  // 🔴 G0.7 Runde 2 (R16 B1): der Name der `tablist` kommt AUSSCHLIESSLICH aus
  // `ariaLabel` — `withPanel` liefert ihn NICHT. Ohne Namen ist die Liste fuer
  // einen Screenreader unbenannt (APG-Abweichung), und zwei Reiterreihen auf
  // einer Seite sind dann ununterscheidbar. Ein fehlender Pflichtwert ist ein
  // Fehler der Aufrufstelle, kein Zustand: dieselbe Form wie `RefreshButton`
  // (`ariaLabel`) und `EmptyState` (`reason`). Ohne `withPanel` aendert sich
  // nichts — die sieben Aufrufer uebergeben es nicht.
  // Der Wurf steht NACH allen Haken: Haken duerfen nicht hinter einem `throw`
  // liegen (`EmptyState.jsx` sagt dasselbe an seinem Wurf).
  if (withPanel && !ariaLabel) {
    throw new Error(
      'UnderlineTabs: `ariaLabel` is required with `withPanel` — `withPanel` does not name the ' +
      'tablist itself, and a `role="tablist"` with no accessible name is an APG deviation.',
    )
  }

  // 🔴 Der Auswaehler (G0.7). Kein `role="tablist"` — es gibt hier keine Reiter;
  // `ariaLabel` benennt den Auswaehler (Vorgabe „Tabs", weil ein namenloses
  // Bedienelement ein Befund waere). Das Paneel bleibt ein gewoehnlicher Kasten:
  // ein `tabpanel` ohne `tablist` waere ein ungueltiges ARIA-Attribut.
  // 🔴 Der Name muss an `inputProps` haengen, nicht am `Select`: der Tragrolle
  // (`role="combobox"`) ist das INNERE Element, ein `aria-label` am `Select`
  // landet am namenlosen Mantel daneben (am DOM gemessen).
  if (auswahl) {
    return (
      <>
        <Box sx={{ ...sx }}>
          <Select
            size="small"
            fullWidth
            inputProps={{ 'aria-label': ariaLabel || 'Tabs' }}
            value={String(value)}
            onChange={e => onChange?.(Number(e.target.value))}
            // 🔴 G0.7 Runde 2 (R16 B2, Begruendung C3 kode-wahr gezogen): derselbe
            // 44-px-Boden wie am Reiter. Der Zweig rendert NUR unter `sm`
            // (`auswahl`, N5.5: grober Zeiger UND unter `sm`) — und genau dort
            // greift der Hausboden fuer Eingabefelder schon
            // (`theme.js` `MuiInputBase.root[down('sm')]`). Das Token ist damit
            // die RUECKVERSICHERUNG: faellt die Breitenklausel je aus `auswahl`,
            // bliebe das Feld ab `sm` sonst bei den 40 px, die `theme.js` fuer
            // `size: 'small'` selbst als „below Rule 22 §8's floor" fuehrt.
            // (Die Runde-1-Begruendung las den Zweig noch zeiger-, nicht
            // breitengebunden — sie beschrieb eine Lage, die es nicht mehr gibt.)
            // 🔴 GEMESSEN, nicht behauptet (T.6g): an dieser Wurzel stehen BEIDE
            // Boeden — der Hausboden (an seiner `max-width`-Bedingung erkannt) UND
            // das Token. Ob MUI die `MuiInputBase`-Overrides auf die Wurzel eines
            // `Select` anwendet, war bis zur Runde 2 offen; sie tut es. Der
            // berechnete Stil entscheidet das NICHT: jsdom wertet Medienabfragen
            // nicht aus (`getComputedStyle` liefert leer, mit und ohne Token) —
            // die Aussage traegt die Regel-Ebene.
            sx={{ fontSize: FS.base, ...TOUCH_TARGET_COARSE }}
          >
            {tabs.map((tab, i) => (
              <MenuItem key={i} value={String(i)} sx={{ fontSize: FS.base }}>
                {zaehler(tab, i) != null ? `${beschriftung(tab)}, ${formatInt(zaehler(tab, i))}` : beschriftung(tab)}
              </MenuItem>
            ))}
          </Select>
        </Box>
        {/* 🔴 G0.7 Runde 2 (R16 W2): hier stand `id={panelId(value)}` — eine
            Kennung, auf die NICHTS zeigte (kein `aria-controls` am `Select`, kein
            `role`, kein `aria-labelledby`). Sie ist weg. Der Zweig traegt bewusst
            KEINE Paneel-Semantik: es gibt hier keine `tablist`, also auch kein
            `tabpanel` — ein `tabpanel` ohne `tablist` waere ein ungueltiges
            ARIA-Attribut. Die Kinder liegen in einem gewoehnlichen Kasten. */}
        {withPanel && <Box>{children}</Box>}
      </>
    )
  }

  return (
    <>
      <Box
        sx={{
          position: 'relative',
          borderBottom: '2px solid',
          borderColor: 'divider',
          // 🔴 M1 (2026-10-09): hier stand `...(rollt ? { overflow: 'hidden' } : {})`.
          // Sein Zweck war richtig — der herausgerollte Teil des Strichs darf die
          // Seite nicht verbreitern —, sein Ort war falsch: der Strich sitzt
          // `bottom: -2`, also AUF dem 2-px-Rand und damit AUSSERHALB der
          // Polsterkante, und `overflow: hidden` schneidet genau dort. Der
          // Aktiv-Strich war im rollenden Modus unsichtbar (gemessen am
          // 2026-10-09 an `/po-inbox` und `/rma/inbox`, Telefon 390 px; auf
          // `/rma/inbox` seit N62, weil `RMAInbox.jsx` dort schon auf `master`
          // `scrollable` setzt). Denselben Zweck erfuellt jetzt `measure`, indem
          // es die Strichgeometrie auf den sichtbaren Bereich KLAMMERT — dort
          // steht auch, warum. Kein zweiter Weg: `overflow: clip` mit
          // `overflowClipMargin` waere die Alternative gewesen, ihre
          // Safari-Unterstuetzung ist ungeklaert.
          ...sx,
        }}
      >
        <Stack
          direction="row"
          role="tablist"
          ref={rowRef}
          onKeyDown={onListKeyDown}
          aria-label={ariaLabel || undefined}
          sx={{
            ...(rollt ? {
              overflowX: 'auto',
              overflowY: 'hidden',
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
            } : {}),
            // 🔴 G0.7 (2026-10-11): die weiche Kante kommt aus `useOverflowMask`
            // (G0.1) — `mask-image`, nicht ein Farbverlauf, weil die Maske keine
            // Oberflaechenfarbe braucht und damit auf jedem Grund richtig ist;
            // `black`/`transparent` sind dort der Alpha-Kanal, kein Farbwert
            // (Regel 22 unberuehrt). Nur wenn die Zeile rollt: ohne Ueberlauf
            // liefert der Hook ohnehin `{}`.
            ...(rollt ? maskSx : {}),
          }}
        >
          {tabs.map((tab, i) => {
            const label = beschriftung(tab)
            const count = zaehler(tab, i)
            const active = i === value

            return (
              <Box
                key={i}
                ref={el => { tabRefs.current[i] = el }}
                role="tab"
                id={withPanel ? reiterId(i) : undefined}
                aria-selected={active}
                // 🔴 G0.7: die Verknuepfung nur am GEWAEHLTEN Reiter — das Paneel
                // der anderen ist nicht gerendert, und ein Verweis ins Leere waere
                // ein ungueltiges ARIA-Attribut (Hausform: `InvoiceList.jsx`
                // `Reiterleiste`).
                aria-controls={withPanel && active ? panelId(i) : undefined}
                tabIndex={active ? 0 : -1}
                aria-label={count != null ? `${label}, ${formatInt(count)}` : undefined}
                onClick={() => onChange?.(i)}
                onKeyDown={e => {
                  if (e.key !== 'Enter' && e.key !== ' ') return
                  e.preventDefault()   // Leertaste auf einem div rollt sonst die Seite
                  onChange?.(i)
                }}
                sx={{
                  px: SP.sm,
                  py: 0.75,
                  cursor: 'pointer',
                  fontSize: FS.base,
                  fontWeight: active ? 600 : 400,
                  color: active ? 'primary.main' : 'text.secondary',
                  userSelect: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  // 🔴 F-2 (R16 W-2, 2026-10-08): der Reiter ist ein BEDIENELEMENT,
                  // und `pattern-library.md` verlangt 44 px Trefferhoehe auf grobem
                  // Zeiger. Bis dahin brachte die Leiste ~32 px mit (py + FS.base),
                  // also UNTER dem Boden — sichtbar geworden, als `NotificationCenter`
                  // vom 44-px-`FilterPillRow` auf diese Leiste umzog. Nur der grobe
                  // Zeiger; auf dem Desktop bleibt die dichte Zeile unveraendert.
                  ...TOUCH_TARGET_COARSE,
                  transition: 'color 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  // K-5 (Welle 2): reine Wirkung (Farbe) ohne Hintergrund.
                  ...hoverOnly({ color: active ? 'primary.main' : 'text.primary' }),
                  ...(rollt ? {
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    // Der globale Fokusring (theme.js: 2 px Breite, 2 px Versatz) liegt
                    // 4 px ausserhalb der Reiterbox. Der scrollende Stack schneidet ihn
                    // oben und unten ab: overflowY ist hidden, und der Reiter fuellt
                    // wegen alignItems: stretch die ganze Stackhoehe. Sichtbar blieben
                    // nur zwei senkrechte Striche. Nach innen versetzt bleibt er ganz.
                    // Nur hier, ohne `scrollable` schneidet nichts. Der Wert kommt
                    // aus dem Token (`FOCUS_RING.innen`), damit er nicht als
                    // Literal neben der Ringquelle steht.
                    '&:focus-visible': { outlineOffset: FOCUS_RING.innen },
                  } : {}),
                }}
              >
                {/* 🔴 G4a-Nachbesserung 4 (P11): die Beschriftung wird in der
                    FETTEN Schnittstelle RESERVIERT. Der aktive Reiter steht in
                    600, die uebrigen in 400 (`fontWeight` unten) — fetter Text
                    ist breiter, und damit aenderte der Reiter beim Wechsel seine
                    eigene Breite: gemessen im `listen`-A/B g4a3 an
                    `/rma/inbox` „All" 90 → 88 px und „Unread (this page)"
                    179 → 182 px, die x-Positionen wanderten um 2 px. N5.3/R2
                    („Reiter springen nicht") verlangt genau das Gegenteil, und
                    `messeP11` misst es (±1 px).
                    🔴 Die Reserve ist der HAUSBAUSTEIN dafuer: ein unsichtbarer
                    Zwilling haelt den Platz (`RangeToggle.jsx:108` macht es mit
                    dem Haekchen genauso). `inline-grid` mit beiden Kindern in
                    DERSELBEN Zelle (`gridArea: 1 / 1`) — die Zelle ist so breit
                    wie das breitere Kind, also wie die fette Fassung, und der
                    sichtbare Text liegt deckungsgleich darueber. Ein
                    `visibility: hidden`-Geschwister im Fluss waere NICHT
                    gegangen: es waere ein zweiter Flex-Kindkasten und bekaeme
                    den `gap` der Zeile dazu.
                    🔴 `aria-hidden` am Zwilling ist PFLICHT, nicht Kosmetik:
                    ohne es laese ein Screenreader die Beschriftung doppelt
                    („UnreadUnread"), und bei einem Reiter OHNE Zaehler ist der
                    Inhalt sein einziger Name. `visibility: hidden` allein
                    genuegt nicht — es nimmt den Text nicht aus dem
                    Zugaenglichkeitsbaum (dieselbe Begruendung wie in
                    `StickyPeekHero`).
                    *Widerlegt haette:* gleiche Reiterbreiten aktiv/inaktiv ohne
                    die Reserve — dann waere die Ursache eine andere. */}
                {reserviert ? (
                  <Box sx={{ display: 'inline-grid' }}>
                    <Box aria-hidden="true" sx={{ gridArea: '1 / 1', fontWeight: 600, visibility: 'hidden' }}>
                      {label}
                    </Box>
                    <Box sx={{ gridArea: '1 / 1', fontWeight: active ? 600 : 400 }}>
                      {label}
                    </Box>
                  </Box>
                ) : label}
                {count != null && (
                  <Box
                    component="span"
                    aria-hidden="true"
                    sx={{
                      fontSize: FS.xs,
                      bgcolor: active ? 'primary.main' : 'action.hover',
                      color: active ? 'primary.contrastText' : 'text.secondary',
                      borderRadius: 99,
                      px: 0.75,
                      lineHeight: 1.6,
                      transition: 'background-color 0.2s ease, color 0.2s ease',
                      // 🔴 G0.7: nur die ausgerichtete Form (`counts`) reserviert den
                      // Platz der breitesten Zahl — sonst springt die Zeile beim
                      // Reiterwechsel (Konzept §5.2, R2). Die alte `{label, count}`
                      // behaelt ihre Darstellung: eine Reserve dort waere eine
                      // sichtbare Aenderung an sieben bestehenden Ansichten (G0-A).
                      // 🔴 G4a-Nachbesserung 4 (P11): die Pille traegt eine
                      // FESTE Schnittstelle. `minWidth` steht in `ch`, und `ch`
                      // ist die Breite der „0" IN DER SCHRIFT DES ELEMENTS —
                      // also auch in SEINEM Schnitt. Ohne diese Zeile erbte die
                      // Pille das `fontWeight` des Reiters, und die fette „0"
                      // ist breiter: die reservierte Breite selbst haette sich
                      // beim Wechsel geaendert und die Reiter waeren weiter
                      // gewandert (die Ursache waere nur von der Beschriftung
                      // auf die Pille umgezogen). 400 ist der Schnitt der
                      // Hausschrift fuer Anzeigetext (`theme.js`); die Pille
                      // steht damit in beiden Zustaenden gleich breit.
                      ...(stellen > 0 ? {
                        minWidth: `${stellen}ch`,
                        textAlign: 'center',
                        fontVariantNumeric: 'tabular-nums',
                        fontWeight: 400,
                      } : {}),
                    }}
                  >
                    {formatInt(count)}
                  </Box>
                )}
              </Box>
            )
          })}
        </Stack>

        {/* Sliding active indicator — sits on top of the 2px divider */}
        <Box
          aria-hidden="true"
          sx={{
            position: 'absolute',
            bottom: -2,
            left: ind.left,
            width: ind.width,
            height: 2,
            bgcolor: 'primary.main',
            borderRadius: '2px 2px 0 0',
            opacity: ready ? 1 : 0,
            transition: ready
              ? 'left 0.25s cubic-bezier(0.4, 0, 0.2, 1), width 0.25s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.15s'
              : 'none',
          }}
        />
      </Box>

      {/* 🔴 G0.7: das Paneel, nur mit `withPanel`. Ein `tabIndex` traegt es NICHT —
          die WAI-ARIA-Praxis setzt ihn nur, wenn das Paneel keine fokussierbaren
          Kinder hat, und das weiss hier allein der Aufrufer. Es steht AUSSERHALB des
          Kastens mit dem 2-px-Rand: der Strich gehoert unter die Reiter, nicht unter
          den Inhalt.
          🔴 S2-NB3 (2026-11-01): `panelAriaDescribedBy` ist die Kennung des Satzes,
          der den Inhalt beschreibt. Ohne die Prop ist der Wert `undefined` — und
          `undefined` setzt KEIN Attribut, der Baum jedes anderen Aufrufers ist
          damit byte-gleich. */}
      {withPanel && (
        <Box
          role="tabpanel"
          id={panelId(value)}
          aria-labelledby={reiterId(value)}
          aria-describedby={panelAriaDescribedBy}
        >
          {children}
        </Box>
      )}
    </>
  )
}

// 🔴 G0.7: ab acht Reitern ist die Reihe eine Liste (Konzept N5.5: „Mehr als sieben
// Reiter … → Auswähler"). Die Zahl steht als Konstante hier und nicht in der
// Bedingung, damit die Herkunft an ihr haengt.
const AUSWAHL_AB = 8
